/**
 * ============================================================================
 * FILTER CONTROL — one inline-labelled pill over the global filterQuery.
 * ============================================================================
 *
 * One compact trigger per property — "Customer: All ▾" — via Cloudscape's
 * inlineLabelText (the label folds INSIDE the control; there is no FormField
 * label row). Clicking the pill opens the standard dropdown: checkboxes,
 * type-to-search, select-all — all native Multiselect (or Select for the
 * single-select properties). Selection IS the property's `=` tokens.
 * Unconstrained shows the explicit `All` placeholder — "not filtered" is a
 * visible state, never an absent chip (spec filter-control-shell R1.2).
 *
 * SELECTED STRIP: every multiselect pill renders its explicit picks as
 * dismissible chips in a sticky header INSIDE the dropdown (above the search
 * box), so a buried pick (DCR ID row 400 of 800) is one click to drop and the
 * list order never changes under the mouse. The pill trigger stays compact.
 */

import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import Select from '@amzn/awsui-components-core/select';
import {
  NOTHING_SELECTED_VALUE,
  selectedValues,
  withSelectedValues,
} from 'src/components/filters/controls/filterControlModel';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';

/** Properties that are single-select in the live dashboard's control grid. */
const SINGLE_SELECT_KEYS = new Set([
  'reportPeriod',
  'recentPeriodsOnly',
  // A single percentile drives the Launch TAT (TP) row — one value, not a set.
  'launchTatPercentile',
]);

export interface FilterControlProps {
  propertyKey: string;
  label: string;
  /** This property's dropdown values (already cascaded by useFilterOptions). */
  values: string[];
  query: FilterQuery;
  onQueryChange: (query: FilterQuery) => void;
  /**
   * VIRTUAL default selection: rendered as checked while the property carries
   * no `=` tokens. Dynamic defaults (the deep dive's Time Period showing the
   * visible window) must not be seeded as real tokens — persisted tokens go
   * stale when the window moves; no tokens = the default follows it. Editing
   * the selection writes real tokens; clearing them returns to the default.
   */
  defaultSelected?: string[];
  /**
   * QuickSight-style exclusive "All" option (deep-dive bars): a real "All"
   * row rendered CHECKED while unconstrained; checking any value unchecks
   * it, and checking "All" again clears every value. Replaces the
   * select-all row (whose semantics are additive, not exclusive).
   */
  exclusiveAllOption?: boolean;
}

/** Sentinel value for the exclusive "All" row (never a real facet value). */
const ALL_OPTION_VALUE = '__all__';

/**
 * Chips shown before "+N more" in the selected strip. The dropdown is the
 * pill's width (215px, filter-shell__pill), which fits two compact chips per
 * row — 4 chips is two rows, so the option list below stays visible. Also
 * bounds the strip's DOM regardless of how many values are picked.
 */
export const SELECTED_STRIP_CHIP_LIMIT = 4;

/** Chips mounted after "+N more": bounds the DOM when hundreds are picked. */
export const SELECTED_STRIP_EXPANDED_LIMIT = 50;

export { NOTHING_SELECTED_VALUE };

interface SelectedStripProps {
  /** Explicit picks (real values only — never the All/None sentinels). */
  picks: string[];
  onRemove: (value: string) => void;
  onClear: () => void;
}

/**
 * The sticky dropdown header: "Selected (N)", Clear, one compact dismissible
 * chip per pick (folded past the limit behind "+N more"; expanded chips scroll
 * inside a 3-row cap so the search box and options never move). Only
 * EXPLICIT picks appear — a virtual default selection is not something the
 * user chose, and dismissing one chip of it would freeze the dynamic window
 * into real tokens. Chips are hand-rolled rather than Cloudscape TokenGroup:
 * its tokens are too wide for the pill-width dropdown (one per row).
 *
 * Focus: always render the host so the search box never remounts, and hand
 * focus back to it before a chip or Clear unmounts (Cloudscape closes on blur).
 */
export const SelectedStrip: React.FC<SelectedStripProps> = ({
  picks,
  onRemove,
  onClear,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const hostRef = React.useRef<HTMLDivElement>(null);
  const hidden = Math.max(0, picks.length - SELECTED_STRIP_CHIP_LIMIT);
  // Fold again once picks drop back under the limit.
  React.useEffect(() => {
    if (hidden === 0) setShowAll(false);
  }, [hidden]);
  const focusSearchBox = () => {
    // Only this dropdown's own search box; no-op when filtering is off.
    hostRef.current
      ?.closest<HTMLElement>('[data-open]')
      ?.querySelector<HTMLInputElement>('input[role="combobox"]')
      ?.focus();
  };
  const remove = (value: string) => {
    focusSearchBox();
    onRemove(value);
  };
  const clear = () => {
    focusSearchBox();
    setShowAll(false);
    onClear();
  };
  const visible = picks.slice(0, showAll ? SELECTED_STRIP_EXPANDED_LIMIT : SELECTED_STRIP_CHIP_LIMIT);
  const notShown = picks.length - visible.length;
  return (
    <div className="filter-control__selected-strip-host" ref={hostRef}>
      {picks.length > 0 && (
        <div className="filter-control__selected-strip" data-testid="selected-strip">
          <div className="filter-control__selected-strip-head">
            <Box
              display="inline-block"
              fontSize="body-s"
              color="text-body-secondary"
              padding={{ right: 's' }}
            >
              {`Selected (${picks.length})`}
            </Box>
            <Button variant="inline-link" onClick={clear}>
              Clear
            </Button>
          </div>
          <div className="filter-control__selected-strip-chips">
            {visible.map((value) => (
              <span className="filter-control__chip" key={value} data-testid="selected-chip">
                <span className="filter-control__chip-label" title={value}>
                  {value}
                </span>
                <Button
                  variant="inline-icon"
                  iconName="close"
                  ariaLabel={`Remove ${value}`}
                  onClick={() => remove(value)}
                />
              </span>
            ))}
            {showAll && notShown > 0 && (
              <Box display="inline-block" fontSize="body-s" color="text-body-secondary">
                {`+${notShown} not shown`}
              </Box>
            )}
            {hidden > 0 && (
              <Button variant="inline-link" onClick={() => setShowAll((open) => !open)}>
                {showAll ? 'Show fewer' : `+${hidden} more`}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const FilterControl: React.FC<FilterControlProps> = ({
  propertyKey,
  label,
  values,
  query,
  onQueryChange,
  defaultSelected,
  exclusiveAllOption,
}) => {
  const explicit = selectedValues(query, propertyKey);
  const selected =
    explicit.length === 0 && defaultSelected?.length
      ? defaultSelected
      : explicit;
  // The selection must stay visible even when the cascade no longer offers
  // its value (e.g. narrowed away by another control) — otherwise the control
  // would LIE about the active state. Selected-but-unoffered values prepend.
  // DEDUPE at this boundary: a value can arrive from both the static seeds and
  // the data-driven pass (Yes/No flags, Customer Type) — one option per value.
  const seen = new Set<string>();
  const optionValues = [
    ...selected.filter((v) => values.indexOf(v) === -1),
    ...values,
  ].filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
  const options = optionValues.map((value) => ({ value, label: value }));
  // Large option sets (the deep-dive DCR ID pill offers every loaded id)
  // render virtualized to stay smooth.
  const large = options.length > 50;

  if (SINGLE_SELECT_KEYS.has(propertyKey)) {
    const current = selected.length > 0 ? selected[0] : null;
    // Cloudscape Select has no clear affordance, so "All" must be a REAL
    // option (mapping to no tokens) or the explicit-All state would be
    // unreachable after the first pick (R1.2).
    const ALL = { value: '', label: 'All' };
    return (
      <Select
        inlineLabelText={label}
        selectedOption={current ? { value: current, label: current } : ALL}
        onChange={({ detail }) =>
          onQueryChange(
            withSelectedValues(
              query,
              propertyKey,
              detail.selectedOption?.value ? [detail.selectedOption.value] : [],
            ),
          )
        }
        options={[ALL, ...options]}
      />
    );
  }

  if (exclusiveAllOption) {
    const unconstrained = explicit.length === 0;
    const isNothing =
      explicit.length === 1 && explicit[0] === NOTHING_SELECTED_VALUE;
    const realSelected = explicit.filter((v) => v !== NOTHING_SELECTED_VALUE);
    const ALL = { value: ALL_OPTION_VALUE, label: 'All' };
    // Options: the sentinel never renders as a row; selected-but-unoffered
    // REAL values still prepend (the control must not lie about the state).
    const exSeen = new Set<string>();
    const exOptions = [
      ...realSelected.filter((v) => values.indexOf(v) === -1),
      ...values,
    ]
      .filter((v) => (exSeen.has(v) ? false : (exSeen.add(v), true)))
      .map((value) => ({ value, label: value }));
    return (
      <Multiselect
        inlineLabelText={label}
        filteringType="auto"
        virtualScroll={large}
        selectedOptions={
          unconstrained
            ? [ALL]
            : realSelected.map((value) => ({ value, label: value }))
        }
        onChange={({ detail }) => {
          const picked = (detail.selectedOptions ?? [])
            .map((o) => o.value)
            .filter((v): v is string => !!v);
          const pickedAll = picked.indexOf(ALL_OPTION_VALUE) !== -1;
          const reals = picked.filter((v) => v !== ALL_OPTION_VALUE);
          // Checking "All" from any constrained state (values or None)
          // clears back to unconstrained. Real picks win over "All" (it is
          // derived from tokens, never stored). Ending with NOTHING checked
          // is the explicit None state: match no rows.
          const next =
            pickedAll && !unconstrained
              ? []
              : reals.length === 0
              ? [NOTHING_SELECTED_VALUE]
              : reals;
          onQueryChange(withSelectedValues(query, propertyKey, next));
        }}
        options={[ALL, ...exOptions]}
        // Sticky strip of real picks. Dropping the LAST pick (or Clear)
        // returns to unconstrained All — the strip removes restrictions; the
        // explicit None state stays reachable only through the checkboxes.
        // Always rendered so the search box never remounts (see SelectedStrip).
        renderDropdownHeader={() => (
          <SelectedStrip
            picks={realSelected}
            onRemove={(value) =>
              onQueryChange(
                withSelectedValues(
                  query,
                  propertyKey,
                  realSelected.filter((v) => v !== value),
                ),
              )
            }
            onClear={() => onQueryChange(withSelectedValues(query, propertyKey, []))}
          />
        )}
        // Unconstrained keeps the SUBTLE placeholder trigger ("All", no
        // highlighted token/count): the checked All row lives only in the
        // dropdown (hideTokens), while real selections render inline.
        placeholder={isNothing ? 'None' : 'All'}
        inlineTokens={!unconstrained}
        hideTokens={unconstrained}
      />
    );
  }

  // A selection identical to the virtual default writes NO tokens — it stays
  // the dynamic default instead of freezing today's values. Shared by the
  // checkbox path and the selected-strip chip path so the two cannot drift.
  const collapseDefault = (picked: string[]): string[] =>
    !!defaultSelected?.length &&
    picked.length === defaultSelected.length &&
    picked.every((v) => defaultSelected.indexOf(v) !== -1)
      ? []
      : picked;

  return (
    <Multiselect
      inlineLabelText={label}
      virtualScroll={large}
      // QuickSight-parity "Select all" row: makes the everything-except-one
      // workflow one uncheck (select all, uncheck the odd value out).
      enableSelectAll={true}
      i18nStrings={{ selectAllText: 'Select all' }}
      selectedOptions={selected.map((value) => ({ value, label: value }))}
      onChange={({ detail }) => {
        const picked = (detail.selectedOptions ?? [])
          .map((o) => o.value)
          .filter((v): v is string => !!v);
        onQueryChange(withSelectedValues(query, propertyKey, collapseDefault(picked)));
      }}
      options={options}
      placeholder="All"
      filteringType="auto"
      // Sticky strip of EXPLICIT picks (the virtual default is not a pick).
      // Removing one writes the remainder; if that equals the virtual default
      // it collapses back to no tokens, same as the checkbox path. Clear
      // drops every token (back to All, or to the dynamic default).
      // Always rendered so the search box never remounts (see SelectedStrip).
      renderDropdownHeader={() => (
        <SelectedStrip
          picks={explicit}
          onRemove={(value) =>
            onQueryChange(
              withSelectedValues(
                query,
                propertyKey,
                collapseDefault(explicit.filter((v) => v !== value)),
              ),
            )
          }
          onClear={() => onQueryChange(withSelectedValues(query, propertyKey, []))}
        />
      )}
      // Selected values render INSIDE the trigger, truncating to "+N"
      // ("Direct, Retail +2") — state stays readable without a token row.
      inlineTokens={true}
    />
  );
};
